import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';
import { MdClose } from 'react-icons/md';
import { Button, IconButton, Spinner } from '@aibuild/ui';
import { trackAction } from '@utils/datadog';
import {
  KIT_METHODS,
  MSG_ERROR,
  MSG_OPTIONS,
  MSG_REPLY,
  MSG_REQUEST,
  MSG_SNAPSHOT,
  MSG_STATE,
  MSG_VISIBILITY,
  SNAPSHOT_SETTLE_MAX_MS,
  type KitMethod,
} from '../../appKit/host';
import { buildSandboxDocument, type HtmlSandboxPolicy, type SandboxKit } from './sandboxDocument';
import {
  FRAME_FEATURES,
  MAX_ERROR_CHARS,
  MAX_ERRORS,
  MAX_PENDING_REQUESTS,
  SANDBOX_PERMISSIONS,
  UNTRUSTED_SANDBOX_PERMISSIONS,
  SNAPSHOT_TIMEOUT_MS,
  SandboxRequestError,
  fixRequestText,
  isRecord,
  type HtmlSandboxError,
  type HtmlSandboxHandle,
  type SandboxBridge,
  type SandboxRequestFailure,
} from './sandboxProtocol';
import {
  SandboxErrorList,
  SandboxFrame,
  SandboxLoading,
  SandboxNotice,
  SandboxPanel,
  SandboxPanelHead,
  SandboxPanelTitle,
  SandboxRoot,
} from './HtmlSandboxViewer.styled';

export interface HtmlSandboxViewerProps {
  html: string;
  policy: HtmlSandboxPolicy;
  kit: SandboxKit;
  agentFree?: boolean;
  bridge?: SandboxBridge;
  title: string;
  /** Mounted but out of sight, so the page can pause what it animates. */
  hidden?: boolean;
  onError?: (error: HtmlSandboxError) => void;
  onRequestFailed?: (failure: SandboxRequestFailure) => void;
  onNavigate?: () => void;
  /** Offers to hand the errors to whoever can fix the page; without it they are only reported. */
  onFixRequest?: (text: string) => void;
  /** The fix action's label, for callers where "Fix this" is not what happens. */
  fixLabel?: string;
  /** The option values the page's kit settled on; untrusted, as everything from the frame. */
  onOptions?: (values: Readonly<Record<string, unknown>>) => void;
  /** The page comes from another organization, through a public link. */
  untrusted?: boolean;
  viewerRef?: Ref<HtmlSandboxHandle>;
}

interface PendingSnapshot {
  resolve: (blob: Blob) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

type Outcome = { ok: true; value: unknown } | { ok: false; error: SandboxRequestError };

const replyMessage = (id: string, outcome: Outcome) =>
  outcome.ok
    ? { type: MSG_REPLY, id, ok: true, value: outcome.value }
    : { type: MSG_REPLY, id, ok: false, error: { code: outcome.error.code, message: outcome.error.message } };

const asRequestError = (error: unknown): SandboxRequestError => {
  if (error instanceof SandboxRequestError) return error;
  return new SandboxRequestError('FAILED', error instanceof Error ? error.message : 'That did not work.');
};

const isKitMethod = (method: unknown): method is KitMethod =>
  typeof method === 'string' && KIT_METHODS.includes(method as KitMethod);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value) || Array.isArray(value)) return false;
  const prototype: unknown = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const sandboxErrorOf = (data: Record<string, unknown>): HtmlSandboxError | null =>
  typeof data.message === 'string'
    ? {
        kind: typeof data.kind === 'string' ? data.kind.slice(0, 20) : 'error',
        message: data.message.slice(0, MAX_ERROR_CHARS),
        source: data.source === 'kit' ? 'kit' : 'page',
      }
    : null;

interface FrameProps extends Omit<HtmlSandboxViewerProps, 'html' | 'policy' | 'kit' | 'agentFree' | 'onNavigate'> {
  srcDoc: string;
  /** The page loaded a second time, which a srcdoc page only does by leaving. */
  onNavigate: () => void;
  notice: React.ReactNode;
}

/** One load of the page. Remounted for a new page or after it navigated. */
const SandboxFrameSession: React.FC<FrameProps> = ({
  srcDoc,
  bridge,
  title,
  hidden = false,
  onError,
  onRequestFailed,
  onFixRequest,
  fixLabel,
  onOptions,
  untrusted = false,
  viewerRef,
  onNavigate,
  notice,
}) => {
  const intl = useIntl();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState<readonly HtmlSandboxError[]>([]);
  const [panelOpen, setPanelOpen] = useState(true);
  const loads = useRef(0);
  const seen = useRef(new Set<string>());
  const pending = useRef(new Map<string, PendingSnapshot>());
  const nextId = useRef(0);
  const onErrorRef = useRef(onError);
  const onRequestFailedRef = useRef(onRequestFailed);
  const onOptionsRef = useRef(onOptions);
  const bridgeRef = useRef(bridge);
  const pendingRequests = useRef(0);

  useEffect(() => {
    onErrorRef.current = onError;
    onRequestFailedRef.current = onRequestFailed;
    onOptionsRef.current = onOptions;
    bridgeRef.current = bridge;
  }, [onError, onRequestFailed, onOptions, bridge]);

  const post = useCallback((message: unknown) => {
    // The frame's origin is opaque, and "*" is the only target that reaches one.
    frameRef.current?.contentWindow?.postMessage(message, '*'); // NOSONAR
  }, []);

  const handleLoad = () => {
    loads.current += 1;
    if (loads.current === 1) setLoaded(true);
    else onNavigate();
  };

  // Coalesced to a frame: a burst of stream events becomes one structured clone.
  const state = bridge?.state;
  useEffect(() => {
    if (!loaded || state === undefined) return;
    const scheduled = requestAnimationFrame(() => post({ type: MSG_STATE, state }));
    return () => cancelAnimationFrame(scheduled);
  }, [loaded, state, post]);

  useEffect(() => {
    if (loaded) post({ type: MSG_VISIBILITY, visible: !hidden });
  }, [loaded, hidden, post]);

  useEffect(() => {
    const reply = (id: string, outcome: Outcome, transfer: Transferable[] = []) => {
      frameRef.current?.contentWindow?.postMessage(replyMessage(id, outcome), '*', transfer); // NOSONAR
    };
    const fail = (id: string, method: unknown, error: SandboxRequestError) => {
      reply(id, { ok: false, error });
      onRequestFailedRef.current?.({
        method: typeof method === 'string' ? method.slice(0, 32) : 'unknown',
        code: error.code,
        message: error.message.slice(0, MAX_ERROR_CHARS),
      });
    };

    const answer = (data: Record<string, unknown>) => {
      const { id, method, params } = data;
      if (typeof id !== 'string' || id.length > 64) return;
      const handler = bridgeRef.current;
      if (!handler || !isKitMethod(method)) {
        fail(id, method, new SandboxRequestError('UNSUPPORTED', 'This page cannot ask for that.'));
        return;
      }
      if (pendingRequests.current >= MAX_PENDING_REQUESTS) {
        fail(id, method, new SandboxRequestError('RATE_LIMITED', 'Too many requests at once.'));
        return;
      }
      pendingRequests.current += 1;
      handler
        .handle(method, params)
        .then(({ value, transfer }) => reply(id, { ok: true, value }, transfer))
        .catch((error: unknown) => fail(id, method, asRequestError(error)))
        .finally(() => {
          pendingRequests.current -= 1;
        });
    };

    const report = (data: Record<string, unknown>) => {
      const error = sandboxErrorOf(data);
      if (!error || seen.current.size >= MAX_ERRORS || seen.current.has(error.message)) return;
      seen.current.add(error.message);
      onErrorRef.current?.(error);
      // The kit's own errors are ours to fix, not the page's author.
      if (error.source === 'page') setErrors((current) => [...current, error]);
    };

    const settleSnapshot = (data: Record<string, unknown>) => {
      const waiting = typeof data.id === 'string' ? pending.current.get(data.id) : undefined;
      if (!waiting) return;
      pending.current.delete(data.id as string);
      clearTimeout(waiting.timer);
      if (data.blob instanceof Blob) waiting.resolve(data.blob);
      else waiting.reject(new Error(typeof data.error === 'string' ? data.error : 'No image'));
    };

    const onMessage = (event: MessageEvent) => {
      // A sandboxed frame's origin is opaque, which serialises as "null".
      if (event.origin !== 'null') return;
      if (event.source !== frameRef.current?.contentWindow) return;
      const data: unknown = event.data;
      if (!isRecord(data)) return;
      if (data.type === MSG_REQUEST) answer(data);
      else if (data.type === MSG_ERROR) report(data);
      else if (data.type === MSG_SNAPSHOT) settleSnapshot(data);
      else if (data.type === MSG_OPTIONS && isPlainObject(data.values)) onOptionsRef.current?.(data.values);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const waiting = pending.current;
    return () => {
      waiting.forEach((entry) => {
        clearTimeout(entry.timer);
        entry.reject(new Error('The page went away'));
      });
      waiting.clear();
    };
  }, []);

  useImperativeHandle(
    viewerRef,
    () => ({
      loaded,
      pageErrors: errors,
      captureFrame: ({ settle = false } = {}) =>
        new Promise<Blob>((resolve, reject) => {
          if (!loaded) {
            reject(new Error('The page has not loaded'));
            return;
          }
          nextId.current += 1;
          const id = `snapshot-${nextId.current}`;
          const timer = setTimeout(
            () => {
              pending.current.delete(id);
              reject(new Error('The page did not answer'));
            },
            SNAPSHOT_TIMEOUT_MS + (settle ? SNAPSHOT_SETTLE_MAX_MS : 0),
          );
          pending.current.set(id, { resolve, reject, timer });
          post({ type: MSG_SNAPSHOT, id, ...(settle && { settle: true }) });
        }),
    }),
    [loaded, errors, post],
  );

  const dismiss = intl.formatMessage({ id: 'htmlSandbox.dismiss', defaultMessage: 'Dismiss' });

  return (
    <>
      <SandboxFrame
        ref={frameRef}
        title={title}
        srcDoc={srcDoc}
        sandbox={untrusted ? UNTRUSTED_SANDBOX_PERMISSIONS : SANDBOX_PERMISSIONS}
        allow={FRAME_FEATURES}
        referrerPolicy="no-referrer"
        onLoad={handleLoad}
        data-testid="html-sandbox__frame"
      />
      {!loaded && (
        <SandboxLoading>
          <Spinner label="" />
        </SandboxLoading>
      )}
      {notice}
      {!notice && onFixRequest && errors.length > 0 && panelOpen && (
        <SandboxPanel role="alert" data-testid="html-sandbox__errors">
          <SandboxPanelHead>
            <SandboxPanelTitle>
              {intl.formatMessage(
                {
                  id: 'htmlSandbox.errors',
                  defaultMessage:
                    '{count, plural, one {This page reported an error} other {This page reported # errors}}',
                },
                { count: errors.length },
              )}
            </SandboxPanelTitle>
            <Button
              variant="outlined"
              size="sm"
              onClick={() => onFixRequest(fixRequestText(errors))}
              dataTestId="html-sandbox__fix"
            >
              {fixLabel ?? intl.formatMessage({ id: 'htmlSandbox.fix', defaultMessage: 'Fix this' })}
            </Button>
            <IconButton
              ariaLabel={dismiss}
              icon={<MdClose size={16} />}
              size="sm"
              onClick={() => setPanelOpen(false)}
            />
          </SandboxPanelHead>
          <SandboxErrorList>
            {errors.map((error) => (
              <li key={error.message}>{error.message}</li>
            ))}
          </SandboxErrorList>
        </SandboxPanel>
      )}
    </>
  );
};

interface Session {
  readonly srcDoc: string;
  readonly id: number;
  readonly navigated: boolean;
}

/**
 * Renders untrusted HTML in a sandboxed `srcdoc` frame. Everything the frame
 * says is treated as untrusted too: only messages from this frame's window are
 * read, and only the fields expected of them.
 */
const HtmlSandboxViewer: React.FC<HtmlSandboxViewerProps> = ({
  html,
  policy,
  kit,
  agentFree = false,
  onNavigate: onLeave,
  ...props
}) => {
  const intl = useIntl();
  const srcDoc = useMemo(
    () => buildSandboxDocument(html, policy, kit, { agentFree }),
    [html, policy, kit, agentFree],
  );
  const [session, setSession] = useState<Session>({ srcDoc, id: 0, navigated: false });
  if (session.srcDoc !== srcDoc) {
    setSession({ srcDoc, id: session.id + 1, navigated: false });
  }

  const onNavigate = useCallback(() => {
    trackAction('app_html_navigation_blocked');
    onLeave?.();
    setSession((current) => ({ ...current, id: current.id + 1, navigated: true }));
  }, [onLeave]);

  const notice = session.navigated ? (
    <SandboxPanel role="status" data-testid="html-sandbox__navigated">
      <SandboxPanelHead>
        <SandboxNotice>
          {intl.formatMessage({
            id: 'htmlSandbox.navigated',
            defaultMessage: 'This page tried to open another address, so it was reloaded.',
          })}
        </SandboxNotice>
        <IconButton
          ariaLabel={intl.formatMessage({ id: 'htmlSandbox.dismiss', defaultMessage: 'Dismiss' })}
          icon={<MdClose size={16} />}
          size="sm"
          onClick={() => setSession((current) => ({ ...current, navigated: false }))}
        />
      </SandboxPanelHead>
    </SandboxPanel>
  ) : null;

  return (
    <SandboxRoot data-testid="html-sandbox">
      <SandboxFrameSession
        key={session.id}
        srcDoc={srcDoc}
        onNavigate={onNavigate}
        notice={notice}
        {...props}
      />
    </SandboxRoot>
  );
};

export default HtmlSandboxViewer;
